// A surface's non-standard paint, complete or not (#666, paint-completeness.ts
// in m2q-pricing): one orange "Non-standard paint" label, for the measurer's
// flag and the desk's paint systems alike, followed by its red "?" or green
// tick, never an icon on its own (DD, 29 Oct), and a red or green edge, so it
// never rests on colour alone. Never hidden.
import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { paintStatesComplete, type PaintStates } from '@higgins/m2q-pricing';
import { completionEdgeColour } from './completion-style';

/** A green tick once complete, else a red "?" (DD, 29 Oct): told apart by shape as well as colour; screen readers hear the word. */
function CompletionLabel({ complete }: { complete: boolean }) {
  const word = complete ? 'complete' : 'incomplete';
  return (
    <span
      role="img"
      aria-label={word}
      title={word}
      className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-xs font-bold leading-none ${complete ? 'bg-green-700 text-white' : 'bg-red-700 text-white'}`}
    >
      {complete ? <Check className="h-3 w-3" strokeWidth={3} aria-hidden /> : '?'}
    </span>
  );
}

/** The orange label for something the desk must finish (the Non-standard paint pill's style). */
const PILL = 'min-w-0 truncate rounded-full bg-orange-50 px-2 text-xs font-normal text-orange-800';

/**
 * "Non-standard paint" and its tick or "?": shown when the surface is flagged or has paint systems, a tick once
 * everything on it is done (paintStatesComplete); nothing otherwise. `label`: with the measurer's note on the Take Off.
 */
export function PaintStatePill({ states, label = 'Non-standard paint', className = '' }: { states: PaintStates; label?: string; className?: string }) {
  const complete = paintStatesComplete(states);
  if (complete === undefined) return null;
  return (
    <span className={`inline-flex min-w-0 max-w-full items-center gap-1 ${className}`}>
      <span className={PILL} title={label}>
        {label}
      </span>
      <CompletionLabel complete={complete} />
    </span>
  );
}

/** Its content with the red or green edge down the left. */
export function CompletionEdge({ complete, children }: { complete: boolean; children: ReactNode }) {
  return <div className={`-ml-1.5 border-l-4 pl-1 ${completionEdgeColour(complete)}`}>{children}</div>;
}
